import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import type { QcChecklistDetail } from './contractsMirror.js';
import { useQcWorkflowMutation } from './useQc.js';

/**
 * The two-stage controls: hand it in, send it back, sign it off.
 *
 * Only ONE action is offered at a time — whichever the checklist's state actually allows — so no
 * button here can be pressed into a refusal. The server re-checks every one of them regardless;
 * this is about not showing somebody a control that cannot work.
 *
 * WHY THE REFUSALS GET REAL COPY. The server answers `unanswered_items` and `outstanding_items`
 * with a COUNT, and that count is the actionable half. "11 items still need an answer" tells a crew
 * what to do; "that didn't work" sends them looking for a bug.
 */

const CONTROL =
  'mt-1 h-11 w-full min-w-0 rounded-md border border-border bg-background px-3 text-foreground text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background';

export interface QcActionsProps {
  data: QcChecklistDetail;
  /** Fail or needs-review, unwaived. Non-zero is what blocks sign-off. */
  outstanding: number;
  unanswered: number;
}

export function QcActions({ data, outstanding, unanswered }: QcActionsProps) {
  const { t } = useTranslation();
  const checklist = data.checklist;
  const workflow = useQcWorkflowMutation(checklist?.id ?? 0);

  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [customerName, setCustomerName] = useState('');

  if (!checklist) return null;
  const canAnswer = data.access?.canAnswer ?? false;
  const canApprove = data.access?.canApprove ?? false;

  const failure = workflow.error;
  const errorMessage =
    failure instanceof ApiError
      ? t(`qc:errors.${failure.message}`, {
          defaultValue: t('qc:errors.generic'),
          count: Number(
            (failure.details?.unanswered as number) ?? (failure.details?.outstanding as number) ?? 0,
          ),
        })
      : null;

  const status = checklist.status;
  const showSubmit = canAnswer && (status === 'draft' || status === 'in_progress' || status === 'changes_requested');
  const showReview = canApprove && status === 'submitted';

  if (!showSubmit && !showReview && status !== 'approved') return null;

  return (
    <section
      aria-label={t('qc:actions.regionLabel')}
      className="sticky bottom-0 mt-4 rounded-md border border-border bg-card p-4 shadow-lg"
    >
      {status === 'approved' ? (
        <p className="text-muted-foreground text-sm">
          {t('qc:actions.approvedNote', { date: checklist.approvedAt ?? '' })}
        </p>
      ) : null}

      {showSubmit ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            disabled={workflow.isPending || unanswered > 0}
            onClick={() => workflow.mutate({ action: 'submit' })}
          >
            {t('qc:actions.submit')}
          </Button>
          {unanswered > 0 ? (
            // Said up front rather than as a refusal after the tap. The button is disabled AND the
            // reason is visible, because a disabled control with no explanation is its own bug.
            <span className="text-muted-foreground text-sm">
              {t('qc:actions.stillUnanswered', { count: unanswered })}
            </span>
          ) : null}
        </div>
      ) : null}

      {showReview ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={workflow.isPending}
              onClick={() => workflow.mutate({ action: 'request-changes' })}
            >
              {t('qc:actions.sendBack')}
            </Button>
            <Button
              type="button"
              disabled={workflow.isPending || outstanding > 0}
              onClick={() => setSigning((v) => !v)}
            >
              {t('qc:actions.signOff')}
            </Button>
            {outstanding > 0 ? (
              <span className="font-medium text-destructive text-sm">
                {t('qc:actions.blockedBy', { count: outstanding })}
              </span>
            ) : null}
          </div>

          {signing && outstanding === 0 ? (
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!signerName.trim()) return;
                workflow.mutate(
                  {
                    action: 'approve',
                    signerName: signerName.trim(),
                    customerName: customerName.trim() || undefined,
                  },
                  { onSuccess: () => setSigning(false) },
                );
              }}
            >
              <div className="w-full sm:w-64">
                <label htmlFor="qc-signer" className="font-medium text-foreground text-xs">
                  {t('qc:actions.signerName')}
                </label>
                <input
                  id="qc-signer"
                  type="text"
                  className={CONTROL}
                  value={signerName}
                  maxLength={160}
                  required
                  onChange={(e) => setSignerName(e.target.value)}
                />
              </div>
              <div className="w-full sm:w-64">
                <label htmlFor="qc-customer" className="font-medium text-foreground text-xs">
                  {t('qc:actions.customerName')}
                </label>
                <input
                  id="qc-customer"
                  type="text"
                  className={CONTROL}
                  value={customerName}
                  maxLength={160}
                  // Optional: a customer is not always at the walkthrough.
                  placeholder={t('qc:actions.customerOptional')}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={workflow.isPending || !signerName.trim()}>
                {t('qc:actions.confirmSignOff')}
              </Button>
            </form>
          ) : null}
        </div>
      ) : null}

      {errorMessage ? (
        <p role="alert" aria-live="assertive" className="mt-3 text-destructive text-sm">
          {errorMessage}
        </p>
      ) : null}
    </section>
  );
}
